import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getUsers, AdminUser } from '../../lib/adminApi';
import { formatDateOnly } from '../../lib/date';

function StatusIndicator({ user }: { user: AdminUser }) {
  if (user.blockedAt) {
    return <span className="admin-pill admin-pill-blocked">Blocked</span>;
  }
  if (user.lockedUntil && new Date(user.lockedUntil) > new Date()) {
    return <span className="admin-pill admin-pill-pending">Locked</span>;
  }
  return null;
}

const PAGE_SIZE = 20;

function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read initial state from URL
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [role, setRole] = useState(searchParams.get('role') ?? '');
  const [verified, setVerified] = useState(searchParams.get('verified') ?? '');
  const [locked, setLocked] = useState(searchParams.get('locked') ?? '');
  const [blocked, setBlocked] = useState(searchParams.get('blocked') ?? '');
  const [createdFrom, setCreatedFrom] = useState(searchParams.get('createdFrom') ?? '');
  const [createdTo, setCreatedTo] = useState(searchParams.get('createdTo') ?? '');
  const [sort, setSort] = useState(searchParams.get('sort') ?? 'newest');

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  // Debounced search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // Sync filters to URL
  useEffect(() => {
    const p: Record<string, string> = {};
    if (debouncedSearch) p.search = debouncedSearch;
    if (role) p.role = role;
    if (verified) p.verified = verified;
    if (locked) p.locked = locked;
    if (blocked) p.blocked = blocked;
    if (createdFrom) p.createdFrom = createdFrom;
    if (createdTo) p.createdTo = createdTo;
    if (sort && sort !== 'newest') p.sort = sort;
    setSearchParams(p, { replace: true });
  }, [debouncedSearch, role, verified, locked, blocked, createdFrom, createdTo, sort, setSearchParams]);

  const fetchUsers = useCallback(async (cursor?: string) => {
    const isLoadMore = !!cursor;
    if (isLoadMore) setLoadingMore(true);
    else {
      setLoading(true);
      setUsers([]);
    }
    setError('');
    try {
      const result = await getUsers({
        search: debouncedSearch || undefined,
        role: role || undefined,
        verified: verified || undefined,
        locked: locked || undefined,
        blocked: blocked || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        sort: sort || undefined,
        limit: PAGE_SIZE,
        cursor: cursor || undefined,
      });
      if (isLoadMore) {
        setUsers(prev => [...prev, ...result.users]);
      } else {
        setUsers(result.users);
      }
      setNextCursor(result.nextCursor);
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, role, verified, locked, blocked, createdFrom, createdTo, sort]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <div>
      <div className="admin-topbar">
        <h1>Users</h1>
      </div>
      <div className="admin-content">
        {/* Filter bar */}
        <div className="admin-filter-bar">
          <div className="admin-filter-group">
            <label className="admin-filter-label">Search</label>
            <input
              className="admin-filter-input"
              placeholder="Email or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 220 }}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Role</label>
            <select
              className="admin-filter-select"
              value={role}
              onChange={e => setRole(e.target.value)}
            >
              <option value="">All</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="guest">Guest</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Verified</label>
            <select
              className="admin-filter-select"
              value={verified}
              onChange={e => setVerified(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Locked</label>
            <select
              className="admin-filter-select"
              value={locked}
              onChange={e => setLocked(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Blocked</label>
            <select
              className="admin-filter-select"
              value={blocked}
              onChange={e => setBlocked(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Created From</label>
            <input
              type="date"
              className="admin-filter-input"
              value={createdFrom}
              onChange={e => setCreatedFrom(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Created To</label>
            <input
              type="date"
              className="admin-filter-input"
              value={createdTo}
              onChange={e => setCreatedTo(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Sort</label>
            <select
              className="admin-filter-select"
              value={sort}
              onChange={e => setSort(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="balance_desc">Balance ↓</option>
              <option value="balance_asc">Balance ↑</option>
              <option value="generations_desc">Generations ↓</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Verified</th>
                    <th>Balance</th>
                    <th>Generations</th>
                    <th>Created</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="admin-table-empty">
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    users.map(u => (
                      <tr key={u.id} onClick={() => navigate(`/admin/users/${u.id}`)}>
                        <td>{u.email}</td>
                        <td>{u.businessName}</td>
                        <td>
                          {u.role === 'admin' ? (
                            <span className="admin-pill admin-pill-admin">Admin</span>
                          ) : (
                            u.role
                          )}
                        </td>
                        <td>
                          {u.emailVerified ? (
                            <span className="admin-pill admin-pill-success">Yes</span>
                          ) : (
                            <span className="admin-pill admin-pill-pending">No</span>
                          )}
                        </td>
                        <td>{u.balance}</td>
                        <td>{u.generationCount}</td>
                        <td>{formatDateOnly(u.createdAt)}</td>
                        <td>
                          <StatusIndicator user={u} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {nextCursor && (
              <div className="admin-load-more">
                <button
                  className="btn-secondary"
                  onClick={() => fetchUsers(nextCursor)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;
